import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import { Spin, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import moment from 'moment';
import formatPrice from '~helpers/currency';
import reconciliationOppStage from '~constants/reconciliation';

const ReconcilitionOpportunityDetails = ({ opportunityDetails }) => {
  const { t } = useTranslation();

  const {
    stage,
    propertyName,
    roomName,
    unitPrice,
    completedXbookingBillingCycle,
    finalMoveInDate,
    finalMoveOutDate,
    landlordApplicationId,
    currency,
    finalPrice,
    completedXbookingReferenceId,
    completedAt,
  } = useMemo(() => {
    const listingMoveIn = moment(get(opportunityDetails, 'completedXbooking.listingMoveIn')).format(
      'DD/MM/YYYY',
    );
    const listingMoveInType = get(opportunityDetails, 'completedXbooking.listingMoveInType');
    const listingMoveOut = moment(
      get(opportunityDetails, 'completedXbooking.listingMoveOut'),
    ).format('DD/MM/YYYY');
    const listingMoveOutType = get(opportunityDetails, 'completedXbooking.listingMoveOutType');

    return {
      stage: get(opportunityDetails, 'stage'),
      heroImageSource: get(opportunityDetails, 'heroImage.source'),
      propertyName: get(opportunityDetails, 'completedXbooking.propertyName'),
      roomName: get(opportunityDetails, 'completedXbooking.roomName'),
      unitPrice: formatPrice(
        get(opportunityDetails, 'completedXbooking.unitPrice'),
        get(opportunityDetails, 'completedXbooking.currency'),
      ),
      completedXbookingBillingCycle: toLower(
        get(opportunityDetails, 'completedXbooking.billingCycle'),
      ),
      listingMoveInDate: listingMoveInType
        ? t(`landlord.booking.detail.value.tenancy.move_in.${toLower(listingMoveInType)}`, {
            moveInDate: listingMoveIn,
          })
        : '-',
      listingMoveOutDate: listingMoveOutType
        ? t(`landlord.booking.detail.value.tenancy.move_out.${toLower(listingMoveOutType)}`, {
            moveOutDate: listingMoveOut,
          })
        : '-',
      finalMoveInDate: get(opportunityDetails, 'finalMoveInDate')
        ? moment(get(opportunityDetails, 'finalMoveInDate')).format('DD/MM/YYYY')
        : '-',
      finalMoveOutDate: get(opportunityDetails, 'finalMoveOutDate')
        ? moment(get(opportunityDetails, 'finalMoveOutDate')).format('DD/MM/YYYY')
        : '-',
      landlordApplicationId: get(opportunityDetails, 'landlordApplicationId') || '-',
      currency: get(opportunityDetails, 'completedXbooking.currency'),
      finalPrice: formatPrice(
        get(opportunityDetails, 'finalPrice'),
        get(opportunityDetails, 'completedXbooking.currency'),
      ),
      completedXbookingReferenceId: get(opportunityDetails, 'completedXbooking.referenceId') || '-',
      completedAt: moment(get(opportunityDetails, 'completedAt')).format('DD/MM/YYYY'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityDetails]);

  return (
    <div
      className={classnames('reconciliation-opportunity-details', {
        'reconciliation-opportunity-details--loading': !opportunityDetails,
      })}
    >
      <If condition={!opportunityDetails}>
        <Spin />
      </If>
      <If condition={opportunityDetails}>
        <div className="reconciliation-opportunity-details__header">
          <h3 className="reconciliation-opportunity-details__title">
            {t('landlord.booking.reconciliation.booking_details.title')}
          </h3>
          <span
            className={classnames('reconciliation-opportunity-details__status', {
              'reconciliation-opportunity-details__status--cancalltion-in-progress':
                stage === reconciliationOppStage.CANCELLATION_IN_PROGRESS,
              'reconciliation-opportunity-details__status--cancalled':
                stage === reconciliationOppStage.CANCELLATION,
            })}
          >
            {stage
              ? t(`landlord.booking.reconciliation.booking_details.status.${toLower(stage)}`)
              : ''}
          </span>
        </div>

        <div className="reconciliation-opportunity-details__content">
          <Row gutter={[32, 29]}>
            <Col span={32}>
              <div className="reconciliation-opportunity-details__property-info">
                <p className="reconciliation-opportunity-details__property-name">{propertyName}</p>
                <p className="reconciliation-opportunity-details__room-info">
                  <span className="reconciliation-opportunity-details__unit-name">{roomName}</span>
                </p>
                <p className="reconciliation-opportunity-details__price-info">
                  <span className="reconciliation-opportunity-details__price">{unitPrice}</span>
                  <span className="reconciliation-opportunity-details__billing-cycle">
                    {` / ${t(
                      `landlord.booking.detail.value.billing_cycle.${completedXbookingBillingCycle}`,
                    )}`}
                  </span>
                </p>
              </div>
            </Col>
          </Row>
          <Row gutter={[32, 16]}>
            <Col span={8}>
              <div className="reconciliation-opportunity-details__label">
                {t('landlord.booking.reconciliation.booking_details.label.actual_move_in_date')}
              </div>
            </Col>
            <Col span={16}>
              <div className="reconciliation-opportunity-details__value">{finalMoveInDate}</div>
            </Col>
          </Row>
          <Row gutter={[32, 16]}>
            <Col span={8}>
              <div className="reconciliation-opportunity-details__label">
                {t('landlord.booking.reconciliation.booking_details.label.actual_move_out_date')}
              </div>
            </Col>
            <Col span={16}>
              <div className="reconciliation-opportunity-details__value">{finalMoveOutDate}</div>
            </Col>
          </Row>
          <Row gutter={[32, 16]}>
            <Col span={8}>
              <div className="reconciliation-opportunity-details__label">
                {t('landlord.booking.reconciliation.booking_details.label.partner_booking_id')}
              </div>
            </Col>
            <Col span={16}>
              <div className="reconciliation-opportunity-details__value">
                {landlordApplicationId}
              </div>
            </Col>
          </Row>
          <Row gutter={[32, 16]}>
            <Col span={8}>
              <div className="reconciliation-opportunity-details__label">
                {t('landlord.booking.reconciliation.booking_details.label.total_price', {
                  currency,
                })}
              </div>
            </Col>
            <Col span={16}>
              <div className="reconciliation-opportunity-details__value">{finalPrice}</div>
            </Col>
          </Row>
          <Row gutter={[32, 16]}>
            <Col span={8}>
              <div className="reconciliation-opportunity-details__label">
                {t('landlord.booking.reconciliation.booking_details.label.scom_booking_id')}
              </div>
            </Col>
            <Col span={16}>
              <div className="reconciliation-opportunity-details__value">
                {completedXbookingReferenceId}
              </div>
            </Col>
          </Row>
          <Row gutter={[32, 16]}>
            <Col span={8}>
              <div className="reconciliation-opportunity-details__label">
                {t('landlord.booking.reconciliation.booking_details.label.complete_at')}
              </div>
            </Col>
            <Col span={16}>
              <div className="reconciliation-opportunity-details__value">{completedAt}</div>
            </Col>
          </Row>
        </div>
      </If>
    </div>
  );
};

ReconcilitionOpportunityDetails.propTypes = {
  opportunityDetails: PropTypes.object,
};

ReconcilitionOpportunityDetails.defaultProps = {
  opportunityDetails: null,
};

export default ReconcilitionOpportunityDetails;
